import type { Prospect } from '@immo/api-contract';
import { PROSPECT_NOTARY_PHASE } from './phase';

// The Notartermin is a zone-less calendar day: parse it as UTC midnight and format in UTC, so no host zone shifts it (BL-041).
const asUtcDay = (iso: string): Date => new Date(`${iso}T00:00:00Z`);

/** `Fr, 13.11.` for the card, `Fr, 13.11.2026` for the detail. */
export function formatNotaryDate(iso: string, locale: string, variant: 'card' | 'detail'): string {
  const day = asUtcDay(iso);
  // de-DE writes „Fr." for the short weekday; the date part already ends in a period.
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(day).replace(/\.$/, '');
  const datePart = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    ...(variant === 'detail' ? { year: 'numeric' as const } : {}),
    timeZone: 'UTC',
  }).format(day);

  return `${weekday}, ${datePart}`;
}

/** `05.11.2026` */
export function formatNotaryDayNumeric(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(
    asUtcDay(iso),
  );
}

/** `13.11.` */
export function formatNotaryDay(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', timeZone: 'UTC' }).format(asUtcDay(iso));
}

// The same „today" rule as the backend's: the Berlin calendar day, whatever the browser's zone.
export function berlinToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export function isNotaryAppointmentPassed(
  prospect: Pick<Prospect, 'status' | 'phase' | 'notaryAppointmentDate'>,
  today: string,
): boolean {
  return (
    prospect.status === 'ACTIVE' &&
    prospect.phase === PROSPECT_NOTARY_PHASE &&
    prospect.notaryAppointmentDate !== null &&
    prospect.notaryAppointmentDate < today
  );
}
